/* eslint-disable camelcase */

import { useState } from "react";
import { useLocalization } from "@fluent/react";
import { IconButton, Row } from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import { modules } from "byzantine";
import Account from "byzantine/src/Account";
import Feature, { featureEquals } from "byzantine/src/Feature";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import MemberFriendSection from "./MemberFriendSection";
import PayeeSection from "./PayeeSection";
import RecipientsSection from "./RecipientsSection";
import WireRecipientSection from "./WireRecipientSection";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";

import type { RecipientsView } from "./RecipientsTable";

export interface RecipientsProps {
  accounts: Account[];
}

const Recipients = ({ accounts }: RecipientsProps) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const institution = useInstitutionSettings();
  const userFeatures = useUserFeatures();
  const hasManageRecipientPermission =
    modules.achPayments.useManageRecipientPermission();
  const hasP2P = featureEquals(userFeatures, "p2p", "m2m");
  const hasWires = userFeatures.wires;
  const hasACH = userFeatures.ach_payments && hasManageRecipientPermission;
  const hasConsumerRecipientFlag = userFeatures.consumer_recipient; // Local flag when building out new recipient redesign for consumer
  const isConsumer = Boolean(currentUser) && !currentUser?.isBusiness();
  const showConsumerRecipients = hasConsumerRecipientFlag && isConsumer;
  const [view, setView] = useState<RecipientsView>("grid");

  let featuresListText: string = "";
  if (showConsumerRecipients && userFeatures.bill_pay && hasP2P && hasWires) {
    featuresListText = l10n.getString("message-linked-recipients-consumer", {
      institutionName: institution.long_name,
    });
  } else if (userFeatures.bill_pay && hasP2P && hasWires && hasACH) {
    featuresListText = l10n.getString(
      "message-linked-recipients-bill-pay-p2p-wire-ach"
    );
  } else if (userFeatures.bill_pay && hasP2P) {
    featuresListText = l10n.getString("message-linked-recipients-bill-pay-p2p");
  } else if (hasP2P && hasWires) {
    featuresListText = l10n.getString("message-linked-recipients-p2p-wire");
  } else if (userFeatures.bill_pay) {
    featuresListText = l10n.getString("message-linked-recipients-bill-pay");
  } else if (hasACH) {
    featuresListText = l10n.getString("message-linked-recipients-ach");
  }

  return (
    <div>
      {showConsumerRecipients ? (
        <Row alignItems="center" className="margin--top--l">
          <Row.Item>
            <div className="recipients-description fontColor--secondary">
              {featuresListText}
            </div>
          </Row.Item>
          <Row.Item shrink>
            <div className="recipients-view-toggle">
              <span
                className={cc({
                  "recipients-view-toggle--selected": view === "grid",
                })}
              >
                <IconButton
                  name="grid"
                  kind="themed"
                  textSize="m"
                  label={l10n.getString("recipients-view-toggle-grid")}
                  aria-pressed={view === "grid"}
                  onClick={() => setView("grid")}
                />
              </span>
              <span
                className={cc({
                  "recipients-view-toggle--selected": view === "list",
                })}
              >
                <IconButton
                  name="menu"
                  kind="themed"
                  textSize="m"
                  label={l10n.getString("recipients-view-toggle-list")}
                  aria-pressed={view === "list"}
                  onClick={() => setView("list")}
                />
              </span>
            </div>
          </Row.Item>
        </Row>
      ) : (
        <div className="margin--top--l">{featuresListText}</div>
      )}
      {hasManageRecipientPermission && showConsumerRecipients ? (
        <Feature
          features={userFeatures}
          or={[
            "ach_payments",
            "wires",
            "international_wires",
            "international_wires_consumer",
          ]}
        >
          <WireRecipientSection view={view} />
        </Feature>
      ) : null}
      <Feature features={userFeatures} flag="bill_pay">
        <PayeeSection view={view} />
      </Feature>
      <Feature features={userFeatures} flag="p2p" equals="m2m">
        <MemberFriendSection accounts={accounts} view={view} />
      </Feature>
      {/* Will remove once the new recipient redesign is fully implemented. */}
      {hasManageRecipientPermission ? (
        <Feature
          features={userFeatures}
          or={[
            "ach_payments",
            "wires",
            "international_wires",
            "international_wires_consumer",
          ]}
        >
          <RecipientsSection />
        </Feature>
      ) : null}
      {/* End of old recipient section */}
    </div>
  );
};

export default Recipients;
